import {
  agentPolicyApi,
  agentPolicyPreviewApi,
  agentRegistryApi,
  type PolicyPayload,
} from '@repo/api/clients/agentPolicy.api';
import { identityApi } from '@repo/api/clients/identity.api';
import { type AgentPolicy } from '@repo/api/schemas/agentPolicy.schema';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import type { PolicyFilterValues } from '../component/PolicyPageFilter/PolicyPageFilter';

import { agentPolicyKeys } from '../data-sources/queryKeys';

export const useAgentPolicyList = (filterValues: PolicyFilterValues) => {
  return useInfiniteQuery({
    queryKey: agentPolicyKeys.policyList(filterValues),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const { searchValue, selectedFilters } = filterValues;
      const { type, status, domain } = selectedFilters;
      // `active` is typed `boolean` in the API client but the BE accepts the same
      // comma-joined string form used for the other multi-select filters. Cast
      // through `unknown` to keep that wire format until the client is widened.
      const payload = {
        type: type?.join(','),
        active: status?.join(',') as unknown as boolean | undefined,
        domain: domain?.join(','),
        searchValue,
      };
      const response = await agentPolicyApi.getPolicyList({
        nextPageToken: pageParam,
        ...payload,
      });
      return {
        policyList: response.data,
        nextPageToken: response.pagination.nextToken,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
    initialPageParam: undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};

export const useAgentPolicyById = (policyId: string) => {
  return useQuery({
    queryKey: agentPolicyKeys.policyById(policyId),
    queryFn: async (): Promise<AgentPolicy> => {
      if (!policyId) {
        throw new Error('policyId is required');
      }
      return await agentPolicyApi.getPolicyById(policyId);
    },
    enabled: !!policyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};

interface UseUpdatePolicyProps {
  onError: (message: string) => void;
  onSuccess?: (payload: { isActive: boolean }) => void;
}

export function useCreatePolicy({ onError, onSuccess }: UseUpdatePolicyProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload }: { payload: PolicyPayload }) =>
      agentPolicyApi.createPolicy({
        payload,
      }),
    onError: () => {
      onError(
        intl.formatMessage({
          id: 'on6hYe',
          defaultMessage: 'Failed to Create policy',
          description: 'Error message for policy Create failure',
        })
      );
    },
    onSuccess: () => {
      // Invalidate the policy query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [...agentPolicyKeys.all, 'policyList'],
      });
      onSuccess?.({ isActive: true });
    },
  });
}

export function useUpdatePolicy({ onError, onSuccess }: UseUpdatePolicyProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      policyId,
      payload,
    }: {
      policyId: string;
      payload: {
        isActive: boolean;
      };
    }) =>
      agentPolicyApi.updatePolicy({
        policyId,
        payload,
      }),
    onError: () => {
      onError(
        intl.formatMessage({
          id: 'j3ym53',
          defaultMessage: 'Failed to update policy',
          description: 'Error message for policy update failure',
        })
      );
    },
    onSuccess: (_data, variables) => {
      // Invalidate the policy query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [...agentPolicyKeys.all, 'policyList'],
      });
      queryClient.invalidateQueries({
        queryKey: agentPolicyKeys.policyById(variables.policyId),
      });
      onSuccess?.(variables.payload);
    },
  });
}

export function useDeletePolicy({
  onError,
  onSuccess,
  onSettled,
}: {
  onError: (message: string) => void;
  onSuccess?: () => void;
  onSettled?: () => void;
}) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (policyId: string) => {
      await agentPolicyApi.deletePolicy(policyId);
    },
    onSuccess: () => {
      // Invalidate the policy query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [...agentPolicyKeys.all, 'policyList'],
      });
      onSuccess?.();
    },
    onError: () => {
      onError(
        intl.formatMessage({
          id: 'a+ziIO',
          defaultMessage: 'Failed to delete policy',
          description: 'Error message for policy delete failure',
        })
      );
    },
    onSettled: () => {
      onSettled?.();
    },
  });
}

/** Hook to load condition field options from registry filter-options API (e.g. target "labels"). Supports search; options can be merged with creatable custom values. Set enabled to false to defer the fetch until e.g. user opens the dropdown. */
export function useFilterOptions(
  target: string,
  searchQuery: string,
  options?: { enabled?: boolean }
) {
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: agentPolicyKeys.filterOptions(target, searchQuery),
    queryFn: async () => {
      const options = await agentRegistryApi.getFilterOptions({
        target,
        searchQuery: searchQuery.trim(),
        pagination: { page: 1, limit: 20 },
      });
      return options;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
  });
}

/** Hook to load identity/owner options from ISPM identity API (POST /ispm/v1/identity). Returns MenuOption[] for owner dropdown. */
export function useIdentityOptions(params?: {
  searchText?: string;
  pageSize?: number;
  enabled?: boolean;
  filters?: unknown[];
}) {
  const {
    searchText = '',
    pageSize = 10,
    enabled = true,
    filters = [],
  } = params ?? {};
  return useQuery({
    queryKey: agentPolicyKeys.identityList({ searchText, filters }),
    queryFn: async () => {
      const options = await identityApi.getIdentityList({
        pageSize,
        searchText: searchText.trim(),
        filters,
      });
      return options;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
  });
}

/** Fetches the evaluations preview for matching agent count. Triggered on demand (mutation) since the call is heavy on the BE. */
export function useEvaluationsPreview() {
  return useMutation({
    mutationFn: (payload: PolicyPayload) =>
      agentPolicyPreviewApi.getEvaluationsPreview(payload),
  });
}
